import { useState } from "react";
import { Play } from "lucide-react";
import { YouTubeVideo } from "@/lib/socialApi";

interface YouTubeVideoCardProps {
    video: YouTubeVideo;
    autoplayOnClick?: boolean;
}

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleDateString('ar-SY', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return '';
    }
};

const YouTubeVideoCard = ({ video, autoplayOnClick = true }: YouTubeVideoCardProps) => {
    const [isPlaying, setIsPlaying] = useState(false);

    const embedSrc = autoplayOnClick
        ? `${video.embedUrl}?autoplay=1&rel=0`
        : `${video.embedUrl}?rel=0`;

    return (
        <article className="group bento-card overflow-hidden flex flex-col">
            <div className="relative aspect-[9/16] bg-black/40 overflow-hidden">
                {isPlaying ? (
                    <iframe
                        src={embedSrc}
                        title={video.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsPlaying(true)}
                        aria-label={`تشغيل الفيديو: ${video.title}`}
                        className="absolute inset-0 w-full h-full group/play"
                    >
                        <img
                            src={video.thumbnail}
                            alt={video.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/play:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-20 h-20 rounded-full bg-primary/90 text-white flex items-center justify-center shadow-2xl transition-all duration-300 group-hover/play:scale-110 group-hover/play:bg-primary">
                                <Play size={36} className="ml-1" fill="currentColor" />
                            </div>
                        </div>
                    </button>
                )}
            </div>
            <div className="p-6 flex-1 flex flex-col gap-3" dir="rtl">
                <h3 className="text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {video.title}
                </h3>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mt-auto">
                    {formatDate(video.publishedAt)}
                </p>
            </div>
        </article>
    );
};

export default YouTubeVideoCard;
